import { type CSSProperties, type RefObject, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './SignedBurst.module.css';

interface Placement {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
  readonly radius: string;
}

interface SignedBurstProps {
  /** Бейдж, вокруг которого рисуем кольца. Позицию берём один раз при монтировании. */
  readonly targetRef: RefObject<HTMLElement | null>;
}

/**
 * Однократное пилюле-кольцо («пинг») вокруг бейджа в момент подписания документа. Рендерится порталом
 * в document.body и позиционируется по координатам бейджа (документные, с учётом скролла), поэтому
 * overflow:hidden карточки его не обрезает и разметку карточки менять не нужно. Кольца повторяют
 * контур бейджа (радиус берём с него). Декоративно: aria-hidden, pointer-events:none; при
 * reduced-motion скрыт целиком (см. CSS).
 */
export default function SignedBurst({ targetRef }: SignedBurstProps) {
  const [placement, setPlacement] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    const target = targetRef.current;
    if (target === null) {
      return;
    }
    const rect = target.getBoundingClientRect();
    setPlacement({
      // Документные координаты (не viewport) → оверлей «приклеен» к бейджу при прокрутке страницы.
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
      radius: getComputedStyle(target).borderTopLeftRadius,
    });
  }, [targetRef]);

  if (placement === null) {
    return null;
  }

  const style: CSSProperties = {
    top: placement.top,
    left: placement.left,
    width: placement.width,
    height: placement.height,
    ['--ring-radius' as string]: placement.radius,
  };

  return createPortal(
    <span className={classes.ripple} style={style} aria-hidden="true" data-testid="signed-burst">
      <span className={classes.ring} />
      <span className={`${classes.ring} ${classes.second}`} />
    </span>,
    document.body,
  );
}
